// @ts-strict-ignore
import { dayjs } from '@/extendedDayjs';
import {
    AppEntity,
    AppPlatform,
    ChecklistItem,
    ChecklistItemStatusType,
    CodeType,
    CommitItem,
    FeatureFlag,
    FeatureFlagStatus,
    FeatureItem,
    FeatureItemCodeStatus,
    PRItem,
    mapCodeStatus,
} from '../types';
import { allCases } from '../typescriptHelpers';

// Please do not change the order of the enum cases as the UI depends on it
export enum SortType {
    TicketOwner = 'ticketOwner',
    CodeAuthor = 'codeAuthor',
    LastUpdated = 'lastUpdated',
    TicketLastUpdated = 'ticketLastUpdated',
    CodeLastUpdated = 'codeLastUpdated',
    TicketStatus = 'ticketStatus',
    CodeStatus = 'codeStatus',
    TicketProject = 'ticketProject',
    TicketEpic = 'ticketEpic',
}

export enum AutomationsToolboxSortType {
    Category = 'category',
    New = 'new',
    Enabled = 'enabled',
}

export enum FeatureFlagsSortType {
    Highlighted = 'highlighted',
    Status = 'status',
    CreatedOn = 'createdOn',
    Owner = 'owner',
    Name = 'name',
}

export enum ApprovalsSortType {
    ItemStatus = 'itemStatus',
    Default = 'default',
}

export enum SettingsSortType {
    Name = 'name',
    EnabledFirst = 'enabledFirst',
    NewUpdatedFirst = 'newUpdatedFirst',
    ChannelOverridesFirst = 'channelOverridesFirst',
    UserOverridesFirst = 'userOverridesFirst',
    TriggerOverridesFirst = 'triggerOverridesFirst',
    SettingsOverridesFirst = 'settingsOverridesFirst',
}

export const AllSortTypes = allCases(SortType);
export const AllAutomationsToolboxSortTypes = allCases(AutomationsToolboxSortType);
export const AllFeatureFlagsSortTypes = allCases(FeatureFlagsSortType);
export const AllApprovalsSortTypes = allCases(ApprovalsSortType);

export const sortFeatureFlagsByStatus: (a: FeatureFlag, b: FeatureFlag) => number = (a, b) => {
    const sortOrder = [
        FeatureFlagStatus.Enabled,
        FeatureFlagStatus.Paused,
        FeatureFlagStatus.Disabled,
        FeatureFlagStatus.Archived,
    ];

    const rank = sortOrder.indexOf(a.status) - sortOrder.indexOf(b.status);

    if (rank === 0) {
        return Date.parse(b.createdAt) - Date.parse(a.createdAt);
    } else {
        return rank;
    }
};

export const sortFunctionForSortType: (sortType: SortType) => (item1: FeatureItem, item2: FeatureItem) => number = (
    sortType,
) => {
    switch (sortType) {
        case SortType.CodeAuthor:
            return sortFeatureItemsByCodeAuthor;
        case SortType.CodeLastUpdated:
            return sortFeatureItemsByCodeLastUpdated;
        case SortType.CodeStatus:
            return sortFeatureItemsByCodeStatus;
        case SortType.LastUpdated:
            return sortFeatureItemsByLastUpdated;
        case SortType.TicketLastUpdated:
            return sortFeatureItemsByTicketLastUpdated;
        case SortType.TicketOwner:
            return sortFeatureItemsByTicketOwner;
        case SortType.TicketStatus:
            return sortFeatureItemsByTicketStatus;
        case SortType.TicketProject:
            return sortFeatureItemsByTicketProject;
        case SortType.TicketEpic:
            return sortFeatureItemsByTicketEpic;
    }
};

// A-Z on a ticket-derived name; items without a name, then items without a ticket, go last
const sortFeatureItemsByTicketName = (nameForTicket: (item: FeatureItem) => string | undefined) => {
    return (item1: FeatureItem, item2: FeatureItem) => {
        if (!item1.ticket || !item2.ticket) {
            return Number(!item1.ticket) - Number(!item2.ticket);
        }
        const name1 = nameForTicket(item1);
        const name2 = nameForTicket(item2);
        if (!name1 || !name2) {
            return Number(!name1) - Number(!name2);
        }
        return name1.localeCompare(name2, undefined, { sensitivity: 'base' });
    };
};

const sortFeatureItemsByTicketProject = sortFeatureItemsByTicketName((item) => item.ticket.project?.displayableName);
const sortFeatureItemsByTicketEpic = sortFeatureItemsByTicketName((item) => item.ticket.epic?.title);

// Keeps members of the same GitHub stack together, ordered from the top of the stack down, anchored
// where the first member of that stack landed in the current sort
export const groupFeatureItemsByStack: (items: FeatureItem[]) => FeatureItem[] = (items) => {
    const groupedItems: FeatureItem[] = [];
    const seenStackIds = new Set<string>();

    items.forEach((item) => {
        const stackId = item.code?.stackInfo?.id;
        if (!stackId) {
            groupedItems.push(item);
            return;
        }

        if (seenStackIds.has(stackId)) {
            return;
        }
        seenStackIds.add(stackId);

        const stackItems = items.filter((otherItem) => otherItem.code?.stackInfo?.id === stackId);
        stackItems.sort((a, b) => (b.code?.stackInfo?.position ?? 0) - (a.code?.stackInfo?.position ?? 0));
        groupedItems.push(...stackItems);
    });

    return groupedItems;
};

export const sortAppsByPlatform: (item1: AppEntity, item2: AppEntity) => number = (item1, item2) => {
    // SDK platforms not included. they should always be in their own app groups
    const platformOrder = [
        AppPlatform.Ios,
        AppPlatform.TvOS,
        AppPlatform.VisionOS,
        AppPlatform.Android,
        AppPlatform.WearOS,
        AppPlatform.ReactNativeOTA,
        AppPlatform.IosSDK,
        AppPlatform.AndroidSDK,
    ];

    const indexOfItem1 = platformOrder.indexOf(item1.appSettings.platform);
    const indexOfItem2 = platformOrder.indexOf(item2.appSettings.platform);

    if (indexOfItem1 !== undefined && indexOfItem2 !== undefined) {
        if (indexOfItem1 < indexOfItem2) {
            return 1;
        } else if (indexOfItem2 < indexOfItem1) {
            return -1;
        } else {
            return 0;
        }
    } else if (indexOfItem1 !== undefined) {
        return 1;
    } else if (indexOfItem2 !== undefined) {
        return -1;
    } else {
        return 0;
    }
};

export const sortFunctionsForApprovalsSortTypes: (
    approvalsSortType: ApprovalsSortType,
) => (item1: ChecklistItem, item2: ChecklistItem) => number = (approvalsSortType) => {
    switch (approvalsSortType) {
        case ApprovalsSortType.ItemStatus:
        case ApprovalsSortType.Default:
            return sortApprovalsByItemStatus;
    }
};

const sortFeatureItemsByLastUpdated = (item1: FeatureItem, item2: FeatureItem) => {
    const item1MostRecentDate = mostRecentFeatureItemDate(item1);
    const item2MostRecentDate = mostRecentFeatureItemDate(item2);

    if (item1MostRecentDate && item2MostRecentDate) {
        if (item2MostRecentDate.isBefore(item1MostRecentDate)) {
            return -1;
        } else if (item1MostRecentDate.isSame(item2MostRecentDate)) {
            return 0;
        } else {
            return 1;
        }
    }

    return 0;
};

const sortFeatureItemsByCodeAuthor = (item1: FeatureItem, item2: FeatureItem) => {
    if (item1.code?.author && item2.code?.author) {
        if (item1.code.author < item2.code.author) {
            return -1;
        } else if (item1.code.author > item2.code.author) {
            return 1;
        }

        return 0;
    } else if (item1.code?.author) {
        return -1;
    } else if (item2.code?.author) {
        return 1;
    }

    return 0;
};

const sortFeatureItemsByTicketLastUpdated = (item1: FeatureItem, item2: FeatureItem) => {
    if (item1.ticket?.updatedAt && item2.ticket?.updatedAt) {
        const item1Moment = dayjs(item1.ticket.updatedAt);
        const item2Moment = dayjs(item2.ticket.updatedAt);
        if (item1Moment.isBefore(item2Moment)) {
            return 1;
        } else if (item1Moment.isAfter(item2Moment)) {
            return -1;
        }

        return 0;
    } else if (item1.ticket?.updatedAt) {
        return -1;
    } else if (item2.ticket?.updatedAt) {
        return 1;
    }

    return 0;
};

const sortFeatureItemsByCodeLastUpdated = (item1: FeatureItem, item2: FeatureItem) => {
    let item1CodeUpdate = null;
    let item2CodeUpdate = null;
    if (item1.code) {
        item1CodeUpdate = mostRecentCodeDate(item1.code);
    }
    if (item2.code) {
        item2CodeUpdate = mostRecentCodeDate(item2.code);
    }
    if (item1CodeUpdate && item2CodeUpdate) {
        const item1Moment = dayjs(item1CodeUpdate);
        const item2Moment = dayjs(item2CodeUpdate);
        if (item1Moment.isBefore(item2Moment)) {
            return 1;
        } else if (item1Moment.isAfter(item2Moment)) {
            return -1;
        }

        return 0;
    } else if (item1CodeUpdate) {
        return -1;
    } else if (item2CodeUpdate) {
        return 1;
    }

    return 0;
};

const sortFeatureItemsByTicketOwner = (item1: FeatureItem, item2: FeatureItem) => {
    if (item1.ticket && item2.ticket) {
        if (item1.ticket.owner && item2.ticket.owner) {
            if (item1.ticket.owner < item2.ticket.owner) {
                return -1;
            } else if (item1.ticket.owner > item2.ticket.owner) {
                return 1;
            }

            return 0;
        } else if (item1.ticket.owner) {
            return -1;
        } else if (item2.ticket.owner) {
            return 1;
        }

        return 0;
    } else if (item1.ticket) {
        return -1;
    } else if (item2.ticket) {
        return 1;
    }

    return 0;
};

const sortFeatureItemsByTicketStatus = (item1: FeatureItem, item2: FeatureItem) => {
    if (item1.ticket?.status && item2.ticket?.status) {
        if (item1.ticket.status < item2.ticket.status) {
            return -1;
        } else if (item1.ticket.status > item2.ticket.status) {
            return 1;
        }

        return 0;
    } else if (item1.ticket?.status) {
        return -1;
    } else if (item2.ticket?.status) {
        return 1;
    }

    return 0;
};

const mostRecentFeatureItemDate: (item: FeatureItem) => dayjs.Dayjs = (item) => {
    const ticketDate = item.ticket?.updatedAt ?? item.ticket?.createdAt;
    let codeDate = null;

    if (item.code) {
        codeDate = mostRecentCodeDate(item.code);
    }

    if (codeDate && ticketDate) {
        const codeDateMoment = dayjs(codeDate);
        const ticketDateMoment = dayjs(ticketDate);

        // Take the most recent date
        if (codeDateMoment.isBefore(ticketDateMoment)) {
            return ticketDateMoment;
        } else if (codeDateMoment.isSame(ticketDateMoment)) {
            return ticketDateMoment;
        } else {
            return codeDateMoment;
        }
    } else if (codeDate) {
        return dayjs(codeDate);
    } else if (ticketDate) {
        return dayjs(ticketDate);
    }

    return null;
};

const featureItemCodeStatusOrder: (status: FeatureItemCodeStatus) => number = (status: FeatureItemCodeStatus) => {
    switch (status) {
        case FeatureItemCodeStatus.OpenPR:
            return 0;
        case FeatureItemCodeStatus.Unmerged:
            return 1;
        case FeatureItemCodeStatus.Merged:
            return 2;
        case FeatureItemCodeStatus.CherryPickOpen:
            return 3;
        case FeatureItemCodeStatus.CherryPickMerged:
            return 4;
        case FeatureItemCodeStatus.CherryPickClosed:
            return 5;
        case FeatureItemCodeStatus.CherryPickSkipped:
            return 6;
        case FeatureItemCodeStatus.CherryPickManuallyResolved:
            return 7;
        case FeatureItemCodeStatus.CherryPickPending:
            return 8;
        case FeatureItemCodeStatus.CherryPickQueued:
            return 9;
        case FeatureItemCodeStatus.CherryPickFailed:
            return 10;
    }
};

const sortFeatureItemsByCodeStatus = (item1: FeatureItem, item2: FeatureItem) => {
    const item1CodeStatus = item1.code?.status;
    const item2CodeStatus = item2.code?.status;
    if (item1CodeStatus && item2CodeStatus) {
        if (
            featureItemCodeStatusOrder(mapCodeStatus(item1CodeStatus)) <
            featureItemCodeStatusOrder(mapCodeStatus(item2CodeStatus))
        ) {
            return -1;
        } else if (
            featureItemCodeStatusOrder(mapCodeStatus(item1CodeStatus)) >
            featureItemCodeStatusOrder(mapCodeStatus(item2CodeStatus))
        ) {
            return 1;
        }

        return 0;
    } else if (item1CodeStatus) {
        return -1;
    } else if (item2CodeStatus) {
        return 1;
    }

    return 0;
};

const mostRecentCodeDate: (code: PRItem | CommitItem) => string = (code) => {
    switch (code.type) {
        case CodeType.Commit: {
            const commitData = code as CommitItem;
            return commitData?.committedAt;
        }
        case CodeType.PullRequest: {
            const prData = code as PRItem;
            return prData?.updatedAt ?? prData?.createdAt;
        }
        default:
            return null;
    }
};

const approvalsItemStatusOrder: (status: ChecklistItemStatusType) => number = (status: ChecklistItemStatusType) => {
    switch (status) {
        case ChecklistItemStatusType.Blocked:
            return 1;
        case ChecklistItemStatusType.InProgress:
            return 2;
        case ChecklistItemStatusType.Rejected:
            return 3;
        case ChecklistItemStatusType.Approved:
            return 4;
        default:
            return 0; // Not Started
    }
};

const sortApprovalsByItemStatus = (item1: ChecklistItem, item2: ChecklistItem) => {
    const item1Status = item1.status?.status;
    const item2Status = item2.status?.status;

    if (approvalsItemStatusOrder(item1Status) < approvalsItemStatusOrder(item2Status)) {
        return -1;
    } else if (approvalsItemStatusOrder(item1Status) > approvalsItemStatusOrder(item2Status)) {
        return 1;
    }

    return 0;
};
